import React, { useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import ReactSVG from 'react-svg';
import { CallMerge, CallSplit } from '@material-ui/icons';
import { Typography } from '@material-ui/core';
import { useControlSessions } from '~/hooks';
import * as lib from '~/dependencies';
import * as actions from '~/store/actions';
import * as actionTypes from '~/store/actions/actionTypes';
import {
  getDescriptiveCallStatus,
  electron,
  isExtension,
  isNotAnonymous,
  formatInternationalNumber,
  formatInternationalE164,
  eventTracker,
} from '~/helpers';
import { integrationContactState } from '~/helpers/contactsIntegration';
import {
  IconTransfer,
  IconHungUpCall,
  IconAnswerCall,
} from '~/assets/svgIcons';
import * as NuacomComponent from '~/components';
import { TimerCount } from '~/components';
import MovablePreference from '../Integrations/MovablePreference';
import * as ActionButtons from './Buttons';
import { sessionActionsTypes } from './session-actions-types';

import {
  Wrapper,
  Container,
  CallItem,
  StatusText,
  FirstRow,
  SecondRow,
  BtnHangUp,
} from './styles';

// routes where headset manager is not allowed
const HEADSET_MANAGER_HIDDEN_ROUTES = [
  /^\/app\/conversations\/[A-Za-z0-9]+\/view$/,
];

export default function ActiveCall() {
  // HOOKS
  const controlSessions = useControlSessions();
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { t } = lib.useTranslation();

  // SELECTORS
  const numberContactToTransfer = useSelector(
    state => state.system.numberContactToTransfer
  );
  const currentSessionId = useSelector(state =>
    state.callCurrent.session ? state.callCurrent.session.id : []
  );
  const calls = useSelector(state => state.callCurrent);
  const integrationsContacts = useSelector(
    state => state.integrations.contacts
  );

  const isIntegrated = useSelector(state => state.integrations.isIntegrated);

  // MEMO's
  const callsMemo = useMemo(() => calls?.data, [calls]);

  // CONST
  const activeCalls = calls?.data;

  const { pathname } = location;
  const isDisplayCall = pathname?.includes('displayCall');

  const isAllowsHeadsetManager = !HEADSET_MANAGER_HIDDEN_ROUTES.some(pattern =>
    pattern.test(pathname)
  );

  const notificationCallControlHandler = useCallback(
    (ev, msg) => {
      if (msg) {
        const callIndex = msg.callId
          ? callsMemo.findIndex(e => e.id === msg.callId)
          : -1;
        if (callIndex !== -1) {
          const action = msg.callAnswer ? msg.callAnswer : msg.callFinish;

          switch (action) {
            case 'Answer':
              controlSessions(msg.callId, sessionActionsTypes.ACCEPT);
              break;
            case 'Decline':
              controlSessions(
                msg.callId,
                callsMemo[callIndex].invite
                  ? sessionActionsTypes.CANCEL
                  : sessionActionsTypes.REJECT
              );
              break;
            case 'Finish':
              controlSessions(msg.callId, sessionActionsTypes.FINISH);
              break;
            default:
          }
        }
      }
    },
    [callsMemo, controlSessions]
  );

  // EFFECTS
  useEffect(() => {
    if (window.isElectron && callsMemo?.length > 0) {
      // This event is emitted when the user Accept/Decline the call from the notification ou pop-up or Jabra device.
      window.ipcRenderer.on(
        'call-electron-notification',
        notificationCallControlHandler
      );
    }

    return () => {
      if (window.isElectron) {
        window.ipcRenderer.removeListener(
          'call-electron-notification',
          notificationCallControlHandler
        );
      }
    };
  }, [callsMemo, notificationCallControlHandler]);

  // useEffect(() => {
  //   // NWA-2054: For a single call, redirect to the display after it finishes
  //   if (
  //     !isDisplayCall &&
  //     activeCalls &&
  //     activeCalls.length === 1 &&
  //     activeCalls[0].status === actionTypes.CALL_FINISHED
  //   ) {
  //     history.push('/app/displayCall');
  //   }
  // }, [isDisplayCall, activeCalls, history]);

  function acceptIncomingCall(session) {
    controlSessions(session.id, sessionActionsTypes.ACCEPT);
  }

  function finishCall(session) {
    navigateToDisplayCall();
    setTimeout(() => {
      if (!session.finished)
        controlSessions(session.id, sessionActionsTypes.FINISH);
    });
  }

  function cancelCall(session) {
    navigateToDisplayCall();
    setTimeout(() => {
      if (!session.finished)
        controlSessions(session.id, sessionActionsTypes.CANCEL);
    });
  }

  function rejectCall(session) {
    if (!session.finished)
      controlSessions(session.id, sessionActionsTypes.REJECT);
  }

  function holdCall(session) {
    if (session.id) controlSessions(session.id, sessionActionsTypes.HOLD);
  }

  function unholdCall(session) {
    // Put on hold any other call which is active.
    callsMemo
      .filter(e => e.id !== session.id && !e.hold)
      .forEach(e => controlSessions(e.id, sessionActionsTypes.HOLD));

    if (session.id) controlSessions(session.id, sessionActionsTypes.UN_HOLD);
    if (session?.mute) unmuteCall(session);
  }

  function muteCall(session) {
    if (session.id) controlSessions(session.id, sessionActionsTypes.MUTE);
  }

  function unmuteCall(session) {
    if (session.id) controlSessions(session.id, sessionActionsTypes.UN_MUTE);
  }

  function splitConferenceCall(session) {
    if (session.id)
      controlSessions(session.id, sessionActionsTypes.SPLIT_CALL_CONFERENCE);
  }

  function mergeCompleteTransfer(session) {
    if (numberContactToTransfer) {
      controlSessions(
        session.id,
        sessionActionsTypes.ATTENDED_TRANSFER,
        numberContactToTransfer.toString()
      );
    }
  }

  function mergeCallConference(session) {
    if (session?.id) {
      const callId = callsMemo.findIndex(e => e.id === session.id);

      if (callsMemo[callId].hold)
        controlSessions(session.id, sessionActionsTypes.UN_HOLD);

      setTimeout(() => {
        controlSessions(session.id, sessionActionsTypes.MERGE_CALL_CONFERENCE);
        clearTimeout(this);
      }, 2500);
    }
  }

  function dtmfHandler(sessionId, action) {
    controlSessions(sessionId, sessionActionsTypes.DTMF_ACTION, action);

    if (action) {
      navigateToDisplayCall();
      eventTracker(events => events.InCallKeypadClicked);
    }
  }

  function expandConferenceToggle(session) {
    eventTracker(events => events.ConferenceButtonClicked);
    navigateToDisplayCall();

    dtmfHandler(session.id, false);

    if (session.callActions.conference) {
      controlSessions(session.id, sessionActionsTypes.CONFERENCE_OFF);
    } else {
      controlSessions(session.id, sessionActionsTypes.CONFERENCE_ON);
    }
  }

  function expandTransferToggle(session) {
    eventTracker(events => events.TransferButtonClicked);
    dtmfHandler(session.id, false);

    const isTransferActive = session.callActions.transfer;

    if (isDisplayCall) {
      controlSessions(
        session.id,
        isTransferActive
          ? sessionActionsTypes.TRANSFER_OFF
          : sessionActionsTypes.TRANSFER_ON
      );
    } else {
      navigateToDisplayCall();

      if (!isTransferActive) {
        dispatch(actions.numberToTransfer());
        controlSessions(session.id, sessionActionsTypes.TRANSFER_ON);
      }
    }
  }

  function expandAnnouncementToggle(session) {
    eventTracker(events => events.AnnouncementButtonClicked);
    navigateToDisplayCall();

    dtmfHandler(session.id, false);

    if (session.callActions.announcement) {
      controlSessions(session.id, sessionActionsTypes.ANNOUNCEMENT_OFF);
    } else {
      controlSessions(session.id, sessionActionsTypes.ANNOUNCEMENT_ON);
    }
  }

  // TODO: Move to navigation hook
  function navigateToDisplayCall() {
    history.push('/app/displayCall');
  }

  function expandTagsToggle(session) {
    dtmfHandler(session.id, false);
    eventTracker(events => events.InCallTagClicked);

    const isTagsActive = session.callActions.tags;

    if (isDisplayCall) {
      controlSessions(
        session.id,
        isTagsActive
          ? sessionActionsTypes.TAGS_OFF
          : sessionActionsTypes.TAGS_ON
      );
    } else {
      navigateToDisplayCall();

      if (!isTagsActive) {
        controlSessions(session.id, sessionActionsTypes.TAGS_ON);
      }
    }
  }

  function expandNotesToggle(session) {
    dtmfHandler(session.id, false);
    eventTracker(events => events.InCallNotesClicked);

    const isNotesActive = session.callActions.notes;

    if (isDisplayCall) {
      controlSessions(
        session.id,
        isNotesActive
          ? sessionActionsTypes.NOTES_OFF
          : sessionActionsTypes.NOTES_ON
      );
    } else {
      navigateToDisplayCall();

      if (!isNotesActive) {
        controlSessions(session.id, sessionActionsTypes.NOTES_ON);
      }
    }
  }

  return (
    <Wrapper onDoubleClick={() => navigateToDisplayCall()}>
      {activeCalls?.length > 0 && isAllowsHeadsetManager && (
        <>
          <div className="deviceManagerBtn">
            <NuacomComponent.HeadsetManager />
          </div>
        </>
      )}

      {activeCalls?.length > 0 &&
        activeCalls.map(e => {
          const callIdFormatted = e.getFormattedCallId();

          const contact = lib._.chain(integrationsContacts)
            .filter(
              c =>
                c.callId === callIdFormatted &&
                c.contactState === integrationContactState.CREATED
            )
            .first()
            .value();

          const isExtendedCard = !!contact;

          // Define which contact name or number must be showed, from CRM integrations, extensions or phonebook.
          const displayName = e?.getDisplayName(contact?.fullNameFormatted);

          // Update Electron notification with new contact name or number
          electron.updateNotificationDisplayName(e.id, e.status, displayName);

          const resume = currentSessionId && currentSessionId === e.id;

          const statusCall = getDescriptiveCallStatus(e.status);

          const showIntegrations =
            isIntegrated &&
            !e.callActions.transfer &&
            !isExtension(e.numberFrom) &&
            isNotAnonymous(e.numberFrom);

          return (
            <Container key={e.id}>
              <CallItem
                active={resume || e.status === actionTypes.CALL_CONFERENCE}>
                <FirstRow>
                  <div className="left">
                    <div>
                      <Typography variant="subtitle1" align="left">
                        <strong>{formatInternationalE164(displayName)}</strong>
                      </Typography>
                    </div>
                    <div className="informations">
                      <Typography variant="subtitle2" align="left">
                        <strong>
                          {formatInternationalNumber(e.numberFrom)}
                        </strong>
                      </Typography>
                    </div>
                  </div>
                  <div className="right">
                    <div className="rightGroup">
                      {e.answered && !e.finished && (
                        <NuacomComponent.RTCStats callId={e.id} />
                      )}
                      <TimerCount
                        answered={e.answered}
                        timerRinging={e.timerRinging}
                        timerDuring={e.timerDuring}
                        id={e.id}
                        callStatus={e.status}
                      />
                    </div>
                    <StatusText>{statusCall}</StatusText>
                  </div>
                </FirstRow>
                <SecondRow
                  answered={e.answered}
                  isExtendedCard={isExtendedCard}
                  dtmf={e.dtmf}>
                  <div className="actions-buttons">
                    {e.status !== actionTypes.CALL_CONFERENCE && (
                      <>
                        <ActionButtons.Transfer
                          session={e}
                          onClick={expandTransferToggle}
                        />

                        {!e.hold && (
                          <ActionButtons.MuteToggle
                            session={e}
                            onClick={e.mute ? unmuteCall : muteCall}
                          />
                        )}

                        <ActionButtons.HoldToggle
                          session={e}
                          onClick={e.hold ? unholdCall : holdCall}
                        />
                      </>
                    )}

                    <ActionButtons.Tags
                      session={e}
                      onClick={expandTagsToggle}
                    />

                    <ActionButtons.Notes
                      session={e}
                      onClick={expandNotesToggle}
                    />

                    {callsMemo.length === 1 && (
                      <ActionButtons.Conference
                        session={e}
                        onClick={expandConferenceToggle}
                      />
                    )}

                    <ActionButtons.InCallKeypad
                      session={e}
                      onClick={dtmfHandler}
                    />

                    <ActionButtons.Announcements
                      session={e}
                      onClick={expandAnnouncementToggle}
                    />

                    <ActionButtons.Contact session={e} />

                    <ActionButtons.SendSMS session={e} />
                  </div>

                  {!e.invite && !e.answered && !e.finished && (
                    <button
                      type="button"
                      onClick={() => acceptIncomingCall(e)}
                      className="action-btn action-one">
                      <div className="green">
                        <ReactSVG src={IconAnswerCall} className="icon-call" />
                      </div>
                    </button>
                  )}
                  {e.readyToMergeCall &&
                    callsMemo.length > 1 &&
                    !e.callActions.conference && (
                      <button
                        className="action-btn action-one"
                        type="button"
                        title={t('components.activeCalls.btn.completeTransfer')}
                        onClick={() => mergeCompleteTransfer(e)}>
                        <div className="green">
                          <ReactSVG
                            src={IconTransfer}
                            className="icon-merge-call-transfer"
                          />
                        </div>
                      </button>
                    )}
                  {e.callActions.conference &&
                    callsMemo[0]?.id === e.id &&
                    callsMemo[1]?.status === 'CALL_IN_PROGRESS' &&
                    e.status !== actionTypes.CALL_CONFERENCE && (
                      <button
                        className="action-btn action-one"
                        type="button"
                        title={t('components.activeCalls.btn.mergeCalls')}
                        onClick={() => mergeCallConference(e)}>
                        <div className="green">
                          <CallMerge className="icon-merge-call-conference" />
                        </div>
                      </button>
                    )}
                  {e.callActions.conference &&
                    callsMemo[0]?.id === e.id &&
                    callsMemo.length > 1 &&
                    e.status === actionTypes.CALL_CONFERENCE && (
                      <button
                        className="action-btn action-one"
                        type="button"
                        title={t('components.activeCalls.btn.splitCalls')}
                        onClick={() => splitConferenceCall(e)}>
                        <div className="green">
                          <CallSplit className="icon-split-call-conference" />
                        </div>
                      </button>
                    )}

                  {e.invite && !e.answered && (
                    <BtnHangUp
                      onClick={() => cancelCall(e)}
                      className="action-btn action-two cancel-call"
                      finished={e.finished}
                      disabled={e.finished}
                      type="button">
                      <div className="red">
                        <ReactSVG src={IconHungUpCall} className="icon-call" />
                      </div>
                    </BtnHangUp>
                  )}

                  {!e.invite && !e.answered && (
                    <BtnHangUp
                      onClick={() => rejectCall(e)}
                      className="action-btn action-two reject-call"
                      finished={e.finished}
                      disabled={e.finished}
                      type="button">
                      <div className="red">
                        <ReactSVG src={IconHungUpCall} className="icon-call" />
                      </div>
                    </BtnHangUp>
                  )}

                  {e.answered && (
                    <BtnHangUp
                      onClick={() => finishCall(e)}
                      className="action-btn action-two finish-call"
                      finished={e.finished}
                      disabled={e.finished}
                      type="button">
                      <div className="red">
                        <ReactSVG src={IconHungUpCall} className="icon-call" />
                      </div>
                    </BtnHangUp>
                  )}
                </SecondRow>

                {showIntegrations && (
                  <MovablePreference callId={callIdFormatted} />
                )}
              </CallItem>
            </Container>
          );
        })}
    </Wrapper>
  );
}
